import {
  Component,
  input,
  output,
  ChangeDetectionStrategy,
  signal,
  computed,
  effect,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { BadgeComponent } from '../badge/badge';
import { EmptyStateComponent } from '../empty-state/empty-state';
import {
  mapRiskLevelToCssClass,
  mapAIRiskLevelToBadgeClass,
  BADGE_CLASS_PREFIX,
  AIRiskLevel,
  RiskLevel,
  AI_RISK_LEVELS,
  RISK_LEVELS,
} from '../../constants/assessment.constants';
import { ASSESSMENT_FRAMEWORK_FULL_NAMES } from '../../utils/assessment.utils';
import { Obligation, ObligationCategory } from '../../models/obligation.model';
import {
  normalizeObligations,
  resolveObligationCategory,
} from '../../utils/obligation-normalize.utils';
import { formatDate } from '../../utils/date.utils';
import { stripInlineMarkdown } from '../../utils/markdown.utils';

/**
 * Camel-cased risk-profile view rendered by `<app-model-card>` and
 * `<app-model-card-summary>`. Scores are non-optional — callers MUST
 * build this via `normalizeAiRiskProfile` (`utils/assessment.utils.ts`),
 * which coerces truly-missing wire values to the enum's `UNKNOWN`
 * member so this contract holds without leaking `undefined` into the
 * render layer.
 */
export interface RiskProfile {
  aiActClassification: AIRiskLevel;
  aiActDescription?: string;
  gdprScore: RiskLevel;
  gdprDescription?: string;
  cybersecurityScore: RiskLevel;
  cybersecurityDescription?: string;
  keyObligations?: Obligation[];
}

type SelectedCard = 'AI_ACT' | 'GDPR' | 'CYBERSECURITY';

/** A working obligation paired with its flat-list index, so edit/save
 *  target the right item regardless of grouping. */
interface ObligationItem {
  obligation: Obligation;
  index: number;
}

/** Render order for the obligation groups (Pre-deployment first). */
const OBLIGATION_CATEGORY_ORDER: ObligationCategory[] = ['PRE_DEPLOYMENT', 'GENERAL'];

@Component({
  selector: 'app-model-card',
  imports: [
    MatIconModule,
    FormsModule,
    BadgeComponent,
    MatMenuModule,
    EmptyStateComponent,
    TranslocoModule,
  ],
  templateUrl: './model-card.html',
  styleUrl: './model-card.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModelCardComponent {
  riskProfile = input<RiskProfile | null>(null);
  assessmentName = input<string>('');
  assessmentDescription = input<string | undefined>(undefined);
  /** Overall-risk verdict shown under the title (from generated_content). */
  headlineVerdict = input<string | undefined>(undefined);
  preparedDate = input<string | null>(null);
  /** Inline-edit gate for descriptions, risk levels, and obligations —
   *  managers + super-managers. Standard users are read-only. */
  canEdit = input<boolean>(false);
  isSavingDescriptions = input<boolean>(false);
  isSavingObligations = input<boolean>(false);

  /** Emits the full description set on each inline save; the parent diffs
   *  against the loaded profile and persists only what changed. */
  descriptionsSaved = output<{
    assessmentDescription: string | null;
    gdprDescription: string | null;
    cybersecurityDescription: string | null;
    aiActDescription: string | null;
    aiActClassification?: AIRiskLevel;
    gdprScore?: RiskLevel;
    cybersecurityScore?: RiskLevel;
  }>();
  /** Emits the full updated obligations list; the parent diffs against the
   *  loaded profile to compute add/update/delete. */
  obligationsSaved = output<Obligation[]>();

  selectedCard = signal<SelectedCard>('AI_ACT');

  // Expose labels and constants to template
  readonly frameworkFullNames = ASSESSMENT_FRAMEWORK_FULL_NAMES;
  readonly badgeClassPrefix = BADGE_CLASS_PREFIX;
  readonly AI_RISK_LEVELS = AI_RISK_LEVELS;
  readonly RISK_LEVELS = RISK_LEVELS;

  readonly issuedDate = computed(() => formatDate(this.preparedDate(), 'long'));

  // Display-only, markdown-stripped copies (BE prose sometimes carries `**…**`).
  // Editing always works off the raw input value, so nothing is lost on save.
  readonly headlineVerdictDisplay = computed(() =>
    stripInlineMarkdown(this.headlineVerdict() ?? '')
  );
  readonly summaryDisplay = computed(() => stripInlineMarkdown(this.assessmentDescription() ?? ''));
  readonly selectedDescriptionDisplay = computed(() =>
    stripInlineMarkdown(this.getSelectedDescription())
  );

  // ── Obligations: working copy + per-card inline editing ──

  /** Local, mutable copy of the obligations, resynced whenever the input
   *  profile changes (e.g. after a save round-trips through the parent). */
  readonly workingObligations = signal<Obligation[]>([]);
  /** Flat index of the obligation currently open for inline editing, or null. */
  readonly editingIndex = signal<number | null>(null);

  readonly categoryOrder = OBLIGATION_CATEGORY_ORDER;

  constructor() {
    effect(() => {
      this.workingObligations.set(normalizeObligations(this.riskProfile()?.keyObligations));
      // A profile refresh (incl. post-save) ends any open inline edit.
      this.editingIndex.set(null);
    });
  }

  readonly obligationCount = computed(() => this.workingObligations().length);

  /** Items in a given category, paired with their flat index. Empty groups
   *  still render (with an "add" affordance) when the user can edit. */
  itemsForCategory(category: ObligationCategory): ObligationItem[] {
    return this.workingObligations()
      .map((obligation, index) => ({ obligation, index }))
      .filter(item => resolveObligationCategory(item.obligation) === category);
  }

  isEditingObligation(index: number): boolean {
    return this.editingIndex() === index;
  }

  getScoreColor(score: string): string {
    return mapRiskLevelToCssClass(score);
  }

  getClassificationColor(classification: string): string {
    return mapAIRiskLevelToBadgeClass(classification);
  }

  // ── Framework cards (expand-to-detail) ──

  selectCard(card: SelectedCard): void {
    // Switching frameworks abandons an in-progress description edit.
    if (this.descEditField() === 'framework') this.cancelDescEdit();
    this.selectedCard.set(card);
  }

  selectedFrameworkFullName(): string {
    switch (this.selectedCard()) {
      case 'AI_ACT':
        return this.frameworkFullNames.AI_ACT;
      case 'GDPR':
        return this.frameworkFullNames.GDPR;
      case 'CYBERSECURITY':
        return this.frameworkFullNames.CYBERSECURITY;
    }
  }

  getSelectedDescription(): string {
    const profile = this.riskProfile();
    if (!profile) return '';

    switch (this.selectedCard()) {
      case 'AI_ACT':
        return profile.aiActDescription || '';
      case 'GDPR':
        return profile.gdprDescription || '';
      case 'CYBERSECURITY':
        return profile.cybersecurityDescription || '';
    }
  }

  // ── Inline description + level editing (super-manager) ──
  // Click a description to edit it in place; picking a risk level from the
  // dropdown saves immediately. Each save emits the full description set;
  // the parent diffs and persists (see `onDescriptionsSaved`).

  readonly descEditField = signal<'summary' | 'framework' | null>(null);
  readonly descEditValue = signal<string>('');

  isEditingSummary(): boolean {
    return this.descEditField() === 'summary';
  }

  isEditingFramework(): boolean {
    return this.descEditField() === 'framework';
  }

  startEditSummary(): void {
    if (!this.canEdit() || this.isSavingDescriptions()) return;
    this.descEditValue.set(this.assessmentDescription() ?? '');
    this.descEditField.set('summary');
  }

  startEditFramework(): void {
    if (!this.canEdit() || this.isSavingDescriptions()) return;
    this.descEditValue.set(this.getSelectedDescription());
    this.descEditField.set('framework');
  }

  updateDescValue(value: string): void {
    this.descEditValue.set(value);
  }

  cancelDescEdit(): void {
    this.descEditField.set(null);
    this.descEditValue.set('');
  }

  /** Current persisted descriptions, as the emit-payload base. */
  private baseDescriptions() {
    const p = this.riskProfile();
    return {
      assessmentDescription: this.assessmentDescription() ?? null,
      gdprDescription: p?.gdprDescription ?? null,
      cybersecurityDescription: p?.cybersecurityDescription ?? null,
      aiActDescription: p?.aiActDescription ?? null,
    };
  }

  saveSummary(): void {
    if (this.isSavingDescriptions()) return;
    this.descriptionsSaved.emit({
      ...this.baseDescriptions(),
      assessmentDescription: this.descEditValue(),
    });
    this.descEditField.set(null);
  }

  saveFramework(): void {
    if (this.isSavingDescriptions()) return;
    const base = this.baseDescriptions();
    const value = this.descEditValue();
    switch (this.selectedCard()) {
      case 'AI_ACT':
        base.aiActDescription = value;
        break;
      case 'GDPR':
        base.gdprDescription = value;
        break;
      case 'CYBERSECURITY':
        base.cybersecurityDescription = value;
        break;
    }
    this.descriptionsSaved.emit(base);
    this.descEditField.set(null);
  }

  setAiRiskLevel(level: AIRiskLevel): void {
    if (!this.canEdit() || this.isSavingDescriptions()) return;
    this.descriptionsSaved.emit({ ...this.baseDescriptions(), aiActClassification: level });
  }

  setGdprScore(score: RiskLevel): void {
    if (!this.canEdit() || this.isSavingDescriptions()) return;
    this.descriptionsSaved.emit({ ...this.baseDescriptions(), gdprScore: score });
  }

  setCybersecurityScore(score: RiskLevel): void {
    if (!this.canEdit() || this.isSavingDescriptions()) return;
    this.descriptionsSaved.emit({ ...this.baseDescriptions(), cybersecurityScore: score });
  }

  // ── Per-obligation inline editing (manager + super-manager) ──

  startEditObligation(index: number): void {
    if (!this.canEdit() || this.isSavingObligations()) return;
    this.editingIndex.set(index);
  }

  cancelEditObligation(): void {
    // Resync from the source of truth to discard unsaved field edits, then close.
    this.workingObligations.set(normalizeObligations(this.riskProfile()?.keyObligations));
    this.editingIndex.set(null);
  }

  private patchObligation(index: number, patch: Partial<Obligation>): void {
    this.workingObligations.update(list => {
      if (index < 0 || index >= list.length) return list;
      const next = [...list];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }

  updateObligationText(index: number, value: string): void {
    this.patchObligation(index, { text: value });
  }

  updateObligationFinding(index: number, value: string): void {
    this.patchObligation(index, { triggering_finding: value });
  }

  updateObligationSteps(index: number, value: string): void {
    this.patchObligation(index, { steps: value });
  }

  addObligationLink(index: number): void {
    const list = this.workingObligations();
    if (index < 0 || index >= list.length) return;
    this.patchObligation(index, { links: [...(list[index].links || []), ''] });
  }

  updateObligationLink(index: number, linkIndex: number, value: string): void {
    const list = this.workingObligations();
    if (index < 0 || index >= list.length) return;
    const links = [...(list[index].links || [])];
    if (linkIndex < 0 || linkIndex >= links.length) return;
    links[linkIndex] = value;
    this.patchObligation(index, { links });
  }

  removeObligationLink(index: number, linkIndex: number): void {
    const list = this.workingObligations();
    if (index < 0 || index >= list.length) return;
    const links = [...(list[index].links || [])];
    if (linkIndex < 0 || linkIndex >= links.length) return;
    links.splice(linkIndex, 1);
    this.patchObligation(index, { links });
  }

  /** Append a blank obligation to the requested group and open it for edit. */
  addObligation(category: ObligationCategory): void {
    if (!this.canEdit() || this.isSavingObligations()) return;
    this.workingObligations.update(list => [
      ...list,
      { text: '', links: [], steps: undefined, triggering_finding: undefined, category },
    ]);
    this.editingIndex.set(this.workingObligations().length - 1);
  }

  /** Persist the current obligation set. Emits the full list minus empty
   *  new rows; the parent computes the add/update/delete delta. */
  saveObligation(): void {
    if (this.isSavingObligations()) return;
    const cleaned = this.workingObligations().filter(
      o =>
        o.text.trim() !== '' ||
        (o.links && o.links.length > 0) ||
        (o.steps && o.steps.trim() !== '')
    );
    this.obligationsSaved.emit(cleaned);
  }

  deleteObligation(index: number): void {
    if (!this.canEdit() || this.isSavingObligations()) return;
    const remaining = this.workingObligations().filter((_, i) => i !== index);
    this.editingIndex.set(null);
    this.obligationsSaved.emit(
      remaining.filter(
        o =>
          o.text.trim() !== '' ||
          (o.links && o.links.length > 0) ||
          (o.steps && o.steps.trim() !== '')
      )
    );
  }
}
